/**
 * WaitlistConfirmPage - Confirm or decline a waitlist reservation
 * for consecutive-day bookings
 * Accessible via /waitlist-confirm?token=xxx&action=confirm|decline
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Calendar, Clock, Check, X, AlertTriangle, Loader2, Dumbbell } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WaitlistConfirmPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const actionFromUrl = searchParams.get('action');
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [reservation, setReservation] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (token) {
      loadReservation();
    } else {
      setError('Kein Reservierungstoken gefunden');
      setLoading(false);
    }
  }, [token]);

  // Auto-process if action is in URL
  useEffect(() => {
    if (reservation && actionFromUrl && !result && !processing) {
      if (actionFromUrl === 'confirm' || actionFromUrl === 'decline') {
        handleAction(actionFromUrl);
      }
    }
  }, [reservation, actionFromUrl]);

  const loadReservation = async () => {
    try {
      const response = await axios.get(`${API}/waitlist/reservation/${token}`);
      setReservation(response.data);
      
      // Check if already processed
      if (response.data.status !== 'pending_confirmation') {
        setResult({
          success: false,
          message: `Diese Reservierung wurde bereits verarbeitet (Status: ${response.data.status})`,
          action: response.data.status
        });
      }
    } catch (error) {
      console.error('Load reservation error:', error);
      if (error.response?.status === 404) {
        setError('Diese Reservierung wurde nicht gefunden oder ist abgelaufen.');
      } else {
        setError('Fehler beim Laden der Reservierung');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/waitlist/reservation/${token}/confirm?action=${action}`
      );
      
      setResult(response.data);
      
      if (response.data.success) {
        if (action === 'confirm') {
          toast.success('Termin erfolgreich bestätigt!');
        } else {
          toast.info('Reservierung abgelehnt');
        }
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Action error:', error);
      const message = error.response?.data?.detail || 'Fehler bei der Verarbeitung';
      toast.error(message);
      setResult({
        success: false,
        message: message,
        action: 'error'
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 text-blue-500 animate-spin mb-4" />
            <p className="text-gray-400">Lade Reservierung...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-red-500 mb-4" />
            <p className="text-white text-center mb-4">{error}</p>
            <Button 
              onClick={() => navigate('/portal')}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Zurück zum Portal
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show result after action
  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardContent className="flex flex-col items-center justify-center py-12">
            {result.action === 'confirmed' ? (
              <>
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                  <Check className="h-8 w-8 text-green-500" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Termin bestätigt!</h2>
                <p className="text-gray-400 text-center mb-6">{result.message}</p>
                {result.appointment && (
                  <div className="bg-gray-700/50 rounded-lg p-4 w-full mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Dumbbell className="h-4 w-4 text-blue-400" />
                      <span className="text-white">{result.appointment.service_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(result.appointment.start_time).toLocaleDateString('de-CH')}</span>
                      <Clock className="h-4 w-4 ml-2" />
                      <span>{new Date(result.appointment.start_time).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>
                )}
              </>
            ) : result.action === 'declined' ? (
              <>
                <div className="w-16 h-16 bg-gray-500/20 rounded-full flex items-center justify-center mb-4">
                  <X className="h-8 w-8 text-gray-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Reservierung abgelehnt</h2>
                <p className="text-gray-400 text-center mb-6">{result.message}</p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-4">
                  <AlertTriangle className="h-8 w-8 text-yellow-500" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Hinweis</h2>
                <p className="text-gray-400 text-center mb-6">{result.message}</p>
              </>
            )}
            <Button 
              onClick={() => navigate('/portal')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Zum Portal
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show confirmation dialog
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="text-center border-b border-gray-700 pb-4">
          <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-8 w-8 text-yellow-500" />
          </div>
          <CardTitle className="text-xl text-white">
            Termin am Folgetag verfügbar
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Dumbbell className="h-5 w-5 text-blue-400" />
              <span className="text-white font-medium">{reservation?.service_name}</span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-300">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span>{reservation?.date}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Clock className="h-4 w-4 text-gray-400" />
                <span>{reservation?.time} Uhr</span>
              </div>
            </div>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
            <p className="text-yellow-200 text-sm">
              <strong>Hinweis:</strong> Du hast bereits einen Termin am Vortag. 
              Wir haben diesen Termin für dich reserviert, aber möchten sicherstellen, 
              dass du an zwei aufeinanderfolgenden Tagen trainieren möchtest.
            </p>
          </div>

          {reservation?.expires_at && (
            <p className="text-xs text-gray-500 text-center mb-6">
              Reservierung gültig bis: {new Date(reservation.expires_at).toLocaleString('de-CH')}
            </p>
          )}

          <div className="flex gap-3">
            <Button
              onClick={() => handleAction('decline')}
              variant="outline"
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <X className="h-4 w-4 mr-2" />
              )}
              Ablehnen
            </Button>
            <Button
              onClick={() => handleAction('confirm')}
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Bestätigen
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WaitlistConfirmPage;
